import { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Images, Loader2, Trash2, Send } from "lucide-react";
import { PhotoLightbox } from "@/components/PhotoLightbox";

const PHASES = [["pickup", "Foto alla consegna"], ["return", "Foto al rientro"]];

// Resize to max side (default 1920px) JPEG so phone photos upload fast; documents use a larger size to keep handwriting legible
export async function compress(file, maxSide = 1920, quality = 0.82) {
  const img = await createImageBitmap(file).catch(() => null);
  if (!img) return file;
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  return blob ? new File([blob], "foto.jpg", { type: "image/jpeg" }) : file;
}

const photoUrl = (bookingId, id) => `${API}/admin/bookings/${bookingId}/photos/${id}?auth=${localStorage.getItem("an_token")}`;
const when = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function PhaseBlock({ bookingId, phase, title, photos, setPhotos, photosSentAt }) {
  const cam = useRef(null);
  const gal = useRef(null);
  const [left, setLeft] = useState(0);
  const [open, setOpen] = useState(null);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setLeft(list.length);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("phase", phase);
        fd.append("file", await compress(f));
        const { data } = await api.post(`/admin/bookings/${bookingId}/photos`, fd);
        setPhotos((p) => [...p, data]);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
      setLeft((n) => n - 1);
    }
    toast.success("Foto salvate");
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    try { await api.delete(`/admin/bookings/${bookingId}/photos/${id}`); setPhotos((p) => p.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const mine = photos.filter((p) => p.phase === phase);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(photosSentAt);
  const send = async () => {
    if (!window.confirm(`Inviare ${mine.length} foto della consegna al cliente via email?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/photos/send`, { origin_url: window.location.origin });
      setSentAt(data.at);
      toast.success(`Foto inviate a ${data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid={`photos-${phase}`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h3 className="font-display font-bold uppercase text-sm mr-auto">{title} <span className="text-muted-foreground font-normal">({mine.length})</span></h3>
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`photos-${phase}-camera-input`} />
        <input ref={gal} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`photos-${phase}-gallery-input`} />
        <div className="flex gap-2 w-full sm:w-auto">
        <Button size="sm" onClick={() => cam.current.click()} disabled={left > 0} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid={`photos-${phase}-camera-btn`}>
          <Camera className="w-4 h-4 mr-1.5" />Scatta foto
        </Button>
        <Button size="sm" variant="outline" onClick={() => gal.current.click()} disabled={left > 0} className="rounded-full border-white/15 bg-transparent" data-testid={`photos-${phase}-gallery-btn`}>
          <Images className="w-4 h-4 mr-1.5" />Galleria
        </Button>
        {phase === "pickup" && mine.length > 0 && (
          <Button size="sm" variant="outline" onClick={send} disabled={sending || left > 0} className="rounded-full border-[#10B981]/40 text-[#10B981] bg-transparent hover:bg-[#10B981]/10" data-testid="photos-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <Send className="w-4 h-4 sm:mr-1.5" />}<span className="hidden sm:inline">Invia al cliente</span><span className="sm:hidden ml-1">Invia</span>
          </Button>
        )}
        </div>
      </div>
      {phase === "pickup" && sentAt && <p className="text-xs text-[#10B981] mb-3" data-testid="photos-sent-at">Inviate al cliente il {when(sentAt)}</p>}
      {left > 0 && <p className="text-xs text-amber-400 mb-3 flex items-center gap-2" data-testid={`photos-${phase}-uploading`}><Loader2 className="w-3.5 h-3.5 animate-spin" />Caricamento… ancora {left}</p>}
      {mine.length === 0 && left === 0 ? (
        <p className="text-xs text-muted-foreground">Nessuna foto. Fotografa i 4 lati, i cerchi, gli interni, il contachilometri e l'indicatore carburante.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {mine.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden bg-black" data-testid={`photo-${p.id}`}>
              <button type="button" onClick={() => setOpen(photoUrl(bookingId, p.id))} className="block w-full" aria-label="Apri foto" data-testid={`photo-open-${p.id}`}>
                <img src={photoUrl(bookingId, p.id)} alt={title} loading="lazy" className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105" />
              </button>
              <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] text-white px-1.5 py-0.5">{when(p.created_at)}</span>
              <button onClick={() => remove(p.id)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-[#E63946] flex items-center justify-center transition-colors" data-testid={`photo-delete-${p.id}`} aria-label="Elimina foto">
                <Trash2 className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
      <PhotoLightbox src={open} onClose={() => setOpen(null)} />
    </div>
  );
}

export const InspectionPhotos = ({ bookingId, photos, setPhotos, photosSentAt }) => (
  <section className="no-print max-w-5xl mx-auto px-4 pb-10 space-y-4" data-testid="inspection-photos">
    <div>
      <h2 className="font-display font-bold uppercase text-lg">Foto del veicolo</h2>
      <p className="text-xs text-muted-foreground">Salvate con data e ora nella prenotazione, utili in caso di contestazioni sui danni. Non vengono stampate nel contratto.</p>
    </div>
    {PHASES.map(([phase, title]) => (
      <PhaseBlock key={phase} bookingId={bookingId} phase={phase} title={title} photos={photos} setPhotos={setPhotos} photosSentAt={photosSentAt} />
    ))}
  </section>
);
